import type { Label } from "@app/core";
import { createLabel, softDeleteLabel, updateLabel } from "@app/core";
import { create } from "zustand";
import { getServices } from "../platform.js";

interface LabelsState {
  loaded: boolean;
  labels: Label[];
  hydrate: () => Promise<void>;
  add: (name: string, color?: string | null) => Promise<Label>;
  rename: (id: string, name: string) => Promise<void>;
  setColor: (id: string, color: string | null) => Promise<void>;
  remove: (id: string) => Promise<void>;
}

// Dedup concurrent hydrate() calls (React StrictMode double-invokes the mount
// effect) so the first-run seed can't insert two sets of default labels in a race.
let inflight: Promise<void> | null = null;

export const useLabelsStore = create<LabelsState>((set, get) => ({
  loaded: false,
  labels: [],

  hydrate() {
    if (inflight) return inflight;
    inflight = hydrateOnce(set).finally(() => { inflight = null; });
    return inflight;
  },

  async add(name, color = null) {
    const { labels } = getServices();
    const l = createLabel({ name, color });
    await labels.insert(l);
    set({ labels: [...get().labels, l] });
    return l;
  },

  async rename(id, name) { await patch(set, get, id, { name }); },
  async setColor(id, color) { await patch(set, get, id, { color }); },

  async remove(id) {
    const { labels } = getServices();
    const current = get().labels.find((l) => l.id === id);
    if (!current) return;
    // Soft-delete. Task `label_ids` keep the now-dangling id — the picker/filter
    // only render resolvable labels, and rewriting every task that referenced it
    // would bump `updated_at` (and future `rev`) on many rows.
    await labels.update(softDeleteLabel(current));
    set({ labels: get().labels.filter((l) => l.id !== id) });
  },
}));

async function hydrateOnce(
  set: (partial: Partial<LabelsState>) => void,
): Promise<void> {
  const { labels } = getServices();
  let list = await labels.listActive();
  // First-run seed: a small starter set distinct from the topic groupings —
  // these are cross-cutting status tags rather than categories.
  if (list.length === 0) {
    const seeded: Label[] = [];
    for (const [name, color] of [
      ["Urgent", "#ef4444"],
      ["Waiting", "#eab308"],
      ["Errand", "#3b82f6"],
    ] as const) {
      const l = createLabel({ name, color });
      await labels.insert(l);
      seeded.push(l);
    }
    list = seeded;
  }
  set({ labels: list, loaded: true });
}

async function patch(
  set: (partial: Partial<LabelsState>) => void,
  get: () => LabelsState,
  id: string,
  fields: Parameters<typeof updateLabel>[1],
): Promise<void> {
  const { labels } = getServices();
  const current = get().labels.find((l) => l.id === id);
  if (!current) return;
  const next = updateLabel(current, fields);
  await labels.update(next);
  set({ labels: get().labels.map((l) => (l.id === id ? next : l)) });
}
